import { useEffect, useRef, useState } from "react"
import { BenefitEditScope, useBenefitEditRegistration } from "@/components/benefit/benefit-edit-scope"
import { IconPlus, IconRefresh, IconSettings } from "@tabler/icons-react"
import { toast } from "sonner"
import { api, ApiError } from "@/lib/api"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { useAppSelector } from "@/store"
import { hasPermission } from "@/store/permissionsSlice"

type Parent = { discriminator: string; label: string }
type Entry = { id: string; parentDiscriminator: string; parentLabel: string; code: string; longLabel: string; isEnabled: boolean; version: number }
type Catalog = { parents: Parent[]; entries: Entry[] }

type Draft = { parentDiscriminator: string; code: string; longLabel: string; isEnabled: boolean; expectedVersion?: number }
const blank: Draft = { parentDiscriminator: "", code: "", longLabel: "", isEnabled: true }

function message(error: unknown) {
  return error instanceof ApiError ? error.message : error instanceof Error ? error.message : "The catalog request failed."
}

export default function IntakeCatalogPage() {
  return <BenefitEditScope><IntakeCatalogEditor /></BenefitEditScope>
}

function IntakeCatalogEditor() {
  const registerEditor = useBenefitEditRegistration()
  const permissions = useAppSelector(state => state.permissions.permissions)
  const canManage = hasPermission(permissions, "intake-catalog:manage")
  const [catalog, setCatalog] = useState<Catalog | null>(null)
  const [draft, setDraft] = useState<Draft>(blank)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [mutationBusy, setMutationBusy] = useState(false)
  const mutationInFlight = useRef(false)
  const hasDraft = editingId !== null || draft.parentDiscriminator !== "" || draft.code !== "" || draft.longLabel !== ""
  useEffect(() => { registerEditor(hasDraft || mutationBusy) }, [registerEditor, hasDraft, mutationBusy])

  const load = async (force = false) => {
    if (mutationBusy && !force) return
    setLoading(true)
    try {
      // Management reads intentionally include retired rows and their frozen versions. The API
      // requires the separate manage permission for this expanded view.
      setCatalog(await api.get<Catalog>("/api/intake-catalog?includeRetired=true"))
    }
    catch (error) { toast.error(message(error)) }
    finally { setLoading(false) }
  }
  useEffect(() => { if (canManage) void load() }, [canManage])

  const save = async () => {
    if (mutationInFlight.current) return
    if (!draft.parentDiscriminator || !draft.code.trim() || !draft.longLabel.trim()) {
      toast.error("Choose a parent and enter both a stable code and label."); return
    }
    if (editingId && draft.expectedVersion == null) {
      toast.error("Reload this row before saving."); return
    }
    mutationInFlight.current = true
    setMutationBusy(true)
    try {
      if (editingId) {
        await api.put<Entry>(`/api/intake-catalog/${editingId}`, { ...draft, version: draft.expectedVersion })
      } else {
        await api.post<Entry>("/api/intake-catalog", draft)
      }
      toast.success(editingId ? "Subtype updated." : "Subtype added.")
      setDraft(blank); setEditingId(null); await load(true)
    } catch (error) { toast.error(message(error)) }
    finally { mutationInFlight.current = false; setMutationBusy(false) }
  }

  const edit = (entry: Entry) => {
    if (mutationInFlight.current) return
    if (hasDraft) {
      toast.error("Save or cancel the open subtype editor before switching rows.")
      return
    }
    // Freeze the exact version and identity at edit start. A later reload/retire cannot replace
    // this expected version with mutable catalog state or silently overwrite another admin.
    setEditingId(entry.id)
    setDraft({ parentDiscriminator: entry.parentDiscriminator, code: entry.code, longLabel: entry.longLabel, isEnabled: entry.isEnabled, expectedVersion: entry.version })
  }

  const toggleEnabled = async (entry: Entry) => {
    if (mutationInFlight.current || hasDraft) return
    mutationInFlight.current = true
    setMutationBusy(true)
    try {
      await api.put<Entry>(`/api/intake-catalog/${entry.id}`, { parentDiscriminator: entry.parentDiscriminator, code: entry.code, longLabel: entry.longLabel, isEnabled: !entry.isEnabled, version: entry.version })
      toast.success(entry.isEnabled ? "Subtype retired." : "Subtype reactivated.")
      await load(true)
    } catch (error) { toast.error(message(error)) }
    finally { mutationInFlight.current = false; setMutationBusy(false) }
  }

  if (!canManage) {
    return <div className="flex flex-col gap-4 py-4 md:py-6 px-4 lg:px-6"><h1 className="text-2xl font-bold">Intake subtype catalog</h1><p className="text-muted-foreground">You do not have permission to manage this catalog.</p></div>
  }

  return <div className="flex flex-col gap-6 py-4 md:py-6 px-4 lg:px-6">
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2"><IconSettings className="size-6" /><div><h1 className="text-2xl font-bold">Intake subtype catalog</h1><p className="text-muted-foreground">Add locations for each intake type, rename their labels, or retire choices.</p></div></div>
      <Button variant="outline" onClick={() => void load()} disabled={loading || mutationBusy}><IconRefresh className="mr-2 size-4" />Reload</Button>
    </div>
    <Card>
      <CardHeader><CardTitle>{editingId ? "Edit subtype" : "Add subtype"}</CardTitle><CardDescription>Stable codes identify rows; changing a label does not rewrite historical applications. Deactivate instead of deleting a referenced row.</CardDescription></CardHeader>
      <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="space-y-1.5"><Label htmlFor="catalog-parent">Parent intake type</Label><Select value={draft.parentDiscriminator || "__empty"} onValueChange={value => setDraft(current => ({ ...current, parentDiscriminator: value === "__empty" ? "" : value }))} disabled={mutationBusy || !!editingId}><SelectTrigger id="catalog-parent"><SelectValue placeholder="Choose parent" /></SelectTrigger><SelectContent><SelectItem value="__empty">Choose parent</SelectItem>{catalog?.parents.map(parent => <SelectItem key={parent.discriminator} value={parent.discriminator}>{parent.label}</SelectItem>)}</SelectContent></Select></div>
        <div className="space-y-1.5"><Label htmlFor="catalog-code">Stable code</Label><Input id="catalog-code" maxLength={50} value={draft.code} disabled={mutationBusy || !!editingId} onChange={event => setDraft(current => ({ ...current, code: event.target.value }))} /></div>
        <div className="space-y-1.5"><Label htmlFor="catalog-label">Display label</Label><Input id="catalog-label" maxLength={100} value={draft.longLabel} disabled={mutationBusy} onChange={event => setDraft(current => ({ ...current, longLabel: event.target.value }))} /></div>
        <div className="flex items-end gap-2"><Button onClick={() => void save()} disabled={mutationBusy || loading}>{editingId ? "Save changes" : <><IconPlus className="mr-2 size-4" />Add subtype</>}</Button>{hasDraft ? <Button variant="ghost" onClick={() => { setEditingId(null); setDraft(blank) }} disabled={mutationBusy}>Cancel</Button> : null}</div>
      </CardContent>
    </Card>
    <Card><CardHeader><CardTitle>Configured subtypes</CardTitle><CardDescription>{catalog?.entries.length ?? 0} catalog relationships</CardDescription></CardHeader><CardContent>{loading ? <p className="text-sm text-muted-foreground">Loading catalog…</p> : <Table><TableHeader><TableRow><TableHead>Parent</TableHead><TableHead>Code</TableHead><TableHead>Label</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader><TableBody>{catalog?.entries.map(entry => <TableRow key={entry.id}><TableCell>{entry.parentLabel}</TableCell><TableCell className="font-mono">{entry.code}</TableCell><TableCell>{entry.longLabel}</TableCell><TableCell>{entry.isEnabled ? "Active" : "Retired"}</TableCell><TableCell className="text-right"><div className="flex justify-end gap-2"><Button variant="outline" size="sm" onClick={() => edit(entry)} disabled={mutationBusy || hasDraft}>Edit</Button><Button variant="ghost" size="sm" onClick={() => void toggleEnabled(entry)} disabled={mutationBusy || hasDraft}>{entry.isEnabled ? "Retire" : "Reactivate"}</Button></div></TableCell></TableRow>)}</TableBody></Table>}</CardContent></Card>
  </div>
}
